// =============================================================================
// Login page — step 0 of the journey
// =============================================================================
// Per BRD §7 the login screen is intentionally minimal. Auth is Google-only
// (locked 2026-07-06): a single "continue with Google" button. Email+password
// was removed to keep the funnel to one low-friction tap and to route every
// supporter through the same OAuth path.
//
// We use a custom button (useSignIn.authenticateWithRedirect) rather than
// Clerk's prebuilt <SignIn />, so the email/password fields never render even
// if the Clerk instance still has that strategy enabled. Google OAuth handles
// both sign-in and sign-up in the same flow. The OAuth round-trip returns to
// /login/sso-callback, where <AuthenticateWithRedirectCallback /> finalizes
// the session and forwards to /signup.
// =============================================================================

import { useEffect, useRef, useState } from "react"
import {
  useSignIn,
  SignedIn,
  SignedOut,
  AuthenticateWithRedirectCallback,
} from "@clerk/clerk-react"
import { Navigate, useLocation } from "react-router-dom"
import { Logo } from "@/components/brand/Logo"
import { resolveRef, persistRef } from "@/lib/referrer"
import { track, sid } from "@/lib/track"

// Multi-color Google "G" mark (official brand colors).
function GoogleMark() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56V6.63H1.26a12 12 0 0 0 0 10.74l4.01-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.26 6.63l4.01 3.09C6.22 6.86 8.87 4.75 12 4.75Z"
      />
    </svg>
  )
}

function GoogleSignInButton() {
  const { signIn, isLoaded } = useSignIn()

  // ⛔ THE LARGEST HOLE IN THE FUNNEL WAS INVISIBLE BECAUSE OF THE LINE BELOW.
  // 44 sessions reached this page and never produced a google_tap. Their shape
  // is not indecision — 42 of the 44 pressed "continue" to get here, and then
  // the whole session lasted 34 seconds against 112 for people who did press.
  // That is someone who found nothing to do.
  //
  // And there IS nothing to do while Clerk is still loading: the button is
  // `disabled`, handleClick early-returns, and a disabled <button> emits no
  // click at all. So a slow or blocked Clerk bundle shows a greyed-out button
  // and swallows every press without a trace. We could not see the press and we
  // could not see the cause.
  //
  // Two measurements, no behaviour change. Whether the button should stay
  // disabled is a product decision and it is Erez's; this only prices it.
  const mountedAt = useRef(Date.now())
  const readyLogged = useRef(false)
  useEffect(() => {
    if (!isLoaded || readyLogged.current) return
    readyLogged.current = true
    track("clerk_ready", String(Date.now() - mountedAt.current))
  }, [isLoaded])

  function handleClick() {
    if (!isLoaded || !signIn) return

    track("google_tap") // instrumentation only — see src/lib/track.ts

    // Thread the invite slug into the OAuth completion URL so Clerk carries it
    // server-side across the flow — this is what survives the mobile in-app
    // browser -> Safari hop, where localStorage/cookies would be lost.
    const ref = resolveRef()
    if (ref) persistRef(ref) // same-browser fallback for the callback leg

    // Thread OUR OWN session id through the round trip, exactly as `ref` is
    // threaded. sessionStorage is what the hop can drop, so without this a
    // person who comes back in a fresh session is indistinguishable from a
    // stranger — and both halves of their journey get scored as failures.
    // Measured 22 Aug: Clerk created 78 accounts in a day where the beacon saw
    // 65 returns. Those 13 were not lost people, they were lost identifiers.
    const params = new URLSearchParams()
    if (ref) params.set("ref", ref)
    params.set("s", sid())
    const complete = `/signup?${params.toString()}`
    void signIn.authenticateWithRedirect({
      strategy: "oauth_google",
      redirectUrl: "/login/sso-callback",
      redirectUrlComplete: complete,
    })
  }

  return (
    // The wrapper exists ONLY to hear the clicks the disabled button cannot
    // report. When Clerk is ready it does nothing at all — the guard is on
    // !isLoaded, so an ordinary press is untouched and reaches the button.
    <div
      onClick={() => {
        if (!isLoaded) track("login_blocked_tap")
      }}
    >
    <button
      type="button"
      onClick={handleClick}
      disabled={!isLoaded}
      className="w-full flex items-center justify-center gap-3 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3.5 text-base font-semibold text-[#0F1B36] shadow-sm transition hover:bg-[#F9FAFB] hover:shadow disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <GoogleMark />
      המשך עם Google
    </button>
    </div>
  )
}


// =============================================================================
// The OAuth return leg
// =============================================================================
// Clerk's <AuthenticateWithRedirectCallback/> is four lines: a useEffect that
// calls handleRedirectCallback(), a promise discarded with `void`, and
// `return null`. It draws NOTHING. So while it works the user sees a blank
// white page, and if the handshake was lost they see that blank page forever.
// That is what supporters reported.
//
// It is lost more often than you would think: Google challenges any browser it
// does not recognise (a private window, an in-app browser, a new phone) and
// sends the 2-Step prompt to the Gmail app ON THE SAME DEVICE. Answering it
// means leaving the browser, and iOS may discard the backgrounded page.
//
// ⛔ THE RULE THIS COMPONENT OBEYS, PAID FOR ON 2026-08-20: NEVER GATE THE
// CRITICAL PATH — ONLY ADD TO IT. An earlier version of this file decided
// whether the URL "looked like" an OAuth return and mounted Clerk's callback
// only if it approved. It was wrong about real returns, so Clerk never
// finalised and NO ACCOUNT WAS EVER CREATED — 70 minutes, ~8 people, zero
// signups. A blank page fails the people who had already failed; a gate fails
// the people who would have succeeded. Below, the callback is mounted
// UNCONDITIONALLY and the success path is untouched. We only draw on top, and
// only act after a timeout that a successful login should never reach.
// Timeout is 5s (Erez, 2026-08-20). A normal callback completes in 1-3s. If it
// fires early on a slow handshake the cost is bounded: /login forwards a
// signed-in user straight to /signup via <SignedIn>, so the worst case is one
// extra redirect, not a lost signup.
//
// WHAT WE DO ON FAILURE: silently restart the login. By then the user has
// already satisfied Google's challenge, so Google trusts the browser and the
// second attempt goes straight through with no prompt at all. And if the
// session did complete late, /login is still the right place to land —
// <SignedIn> forwards to /signup. Either way they end up where they wanted.
// =============================================================================

const SSO_TIMEOUT_MS = 5000
const RETRY_KEY = "__degel_sso_retry_at"
const RETRY_COOLDOWN_MS = 60000 // never bounce in a loop

function SsoCallback() {
  const [stalled, setStalled] = useState(false)
  const [retryExhausted, setRetryExhausted] = useState(false)

  // Instrumentation only, in its own effect so it cannot interact with the
  // timeout below. This is THE measurement we were missing: a tap with no
  // callback_seen means they never came back from Google; a callback_seen
  // with no Clerk user means they came back and WE failed them. Until now
  // both were scored as Google's fault.
  useEffect(() => {
    track("callback_seen")
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setStalled(true), SSO_TIMEOUT_MS)
    return () => clearTimeout(t)
  }, [])

  // Reaching the timeout is itself the finding. `callback_seen` said someone
  // came back from Google; it never said what happened next, so six sessions a
  // day that came back and never reached the form could not be told apart —
  // Clerk failing to finalise, or a person leaving. Now they can.
  useEffect(() => {
    if (stalled) track("callback_stall")
  }, [stalled])

  useEffect(() => {
    if (!stalled) return
    let last = 0
    try {
      last = Number(sessionStorage.getItem(RETRY_KEY) || 0)
    } catch {
      /* private mode can throw on storage — treat as "never retried" */
    }
    if (Date.now() - last < RETRY_COOLDOWN_MS) {
      track("callback_exhausted")
      setRetryExhausted(true)
      return
    }
    try {
      sessionStorage.setItem(RETRY_KEY, String(Date.now()))
    } catch {
      /* if we cannot record it, still retry once rather than dead-end */
    }
    track("callback_retry")
    window.location.replace("/login")
  }, [stalled])

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF7] to-white flex flex-col">
      <header className="px-6 lg:px-10 py-6">
        <Logo size="md" />
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md text-center">
          {/* ALWAYS mounted. Never conditional. See the rule above. */}
          <AuthenticateWithRedirectCallback />

          {!retryExhausted ? (
            <>
              <div
                className="mx-auto mb-4 size-8 animate-spin rounded-full border-2 border-[#E5E7EB] border-t-[#1B2D52]"
                aria-hidden="true"
              />
              <p className="text-base text-[#6B7280]">מתחברים… רגע אחד</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold text-[#0F1B36] mb-3">
                כמעט שם
              </h1>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-6">
                ההתחברות נקטעה. לחצו כדי להמשיך — הפעם זה אמור לעבור מיד.
              </p>
              <a
                href="/login"
                className="inline-block w-full rounded-xl bg-[#1B2D52] px-4 py-3 text-base font-semibold text-white"
              >
                המשך להתחברות
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  )
}

export function LoginPage() {
  const location = useLocation()
  const isCallback = location.pathname.includes("sso-callback")

  // Instrumentation only — see src/lib/track.ts. THE question this answers:
  // when someone taps Google and never comes back, do they ever return to this
  // page? If they do, an automatic retry reaches them and costs the user
  // nothing. If they do not, they are lost inside Google's own pages and no
  // change on our side can find them. On 21 Aug, 30 of 42 first attempts
  // failed and only 2 ever tried again — this is how we learn where the other
  // 28 went.
  useEffect(() => {
    if (!isCallback) track("login_view")
  }, [isCallback])

  // OAuth return leg — let Clerk finalize the session, then it forwards to the
  // `redirectUrlComplete` set at sign-in (which carries `?ref=…`). We do NOT
  // pass force-redirect URLs here: a static `/signup` would strip the slug that
  // Clerk carried through the flow — the whole point of the fix.
  if (isCallback) {
    return <SsoCallback />
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF7] to-white flex flex-col">
      <header className="px-6 lg:px-10 py-6">
        <Logo size="md" />
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <SignedOut>
          <div className="w-full max-w-md">
            <h1 className="text-3xl font-extrabold text-center text-[#0F1B36] mb-2 leading-tight">
              ברוך/ה הבא/ה
            </h1>
            <p className="text-center text-sm text-[#6B7280] mb-6">
              כניסה מהירה עם חשבון Google — ומוסיפים את הדגל שלך למפה.
            </p>
            <GoogleSignInButton />
          </div>
        </SignedOut>
        <SignedIn>
          <Navigate to="/signup" replace />
        </SignedIn>
      </main>
    </div>
  )
}
